import { useEffect, useMemo } from 'react'
import { useDashboardLayoutStore } from '@/stores/useDashboardLayoutStore'
import {
    DndContext,
    closestCenter,
    DragOverlay,
    useDroppable,
} from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'
import { WidgetStoreDrawer } from './WidgetStore'
import { useDashboardTour } from './use-dashboard-tour'
import { useTourHandler } from '@/contexts/tour-handler'
import { useLayoutStorage } from './hooks/use-layout-storage'
import { useWidgetManagement } from './hooks/use-widget-management'
import { useDragAndDrop } from './hooks/use-drag-and-drop'
import { SortableCard } from './components/sortable-card'
import { TrashZone } from './components/trash-zone'
import { getGridSizeClass } from './utils/grid-size-utils'
import type { DraggableLayoutProps } from './types'

// Export types for external use
export type { Layouts, LayoutItem } from './types'

const DraggableLayout: React.FC<DraggableLayoutProps> = ({
    widgets: initialWidgets,
    storageKey,
    department,
}) => {
    const { isLocked, setStorageKey, setCurrentDepartment } =
        useDashboardLayoutStore()

    // Dashboard tour
    const { startTour } = useDashboardTour({
        department: department || 'GENERAL',
    })
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Register tour handler for TourHint
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    // Layout storage
    const { loadSavedLayout, saveLayout } = useLayoutStorage({
        storageKey,
        initialWidgets,
    })

    // Widget management
    const {
        dashboardWidgets,
        setDashboardWidgets,
        activeWidgetIds,
        initialWidgetIds,
        handleAddWidget,
        handleResize,
    } = useWidgetManagement({
        initialWidgets,
        loadSavedLayout,
        saveLayout,
    })

    // Drag and drop
    const { activeId, sensors, handleDragStart, handleDragEnd } =
        useDragAndDrop({
            onAddWidget: handleAddWidget,
            setDashboardWidgets,
            saveLayout,
        })

    // Register storageKey in the store on mount
    useEffect(() => {
        setStorageKey(storageKey)
    }, [storageKey, setStorageKey])

    // Set current department for widget store filtering
    useEffect(() => {
        if (department) {
            setCurrentDepartment(department)
        }
    }, [department, setCurrentDepartment])

    const sortedWidgets = useMemo(() => {
        return [...dashboardWidgets]
            .filter((w) => w.isVisible)
            .sort((a, b) => a.order - b.order)
    }, [dashboardWidgets])

    const activeWidget = activeId
        ? dashboardWidgets.find((w) => w.id === activeId)
        : null

    // Droppable zone for dashboard grid (to accept store widgets)
    const { setNodeRef: setDroppableRef, isOver: isOverDashboard } =
        useDroppable({
            id: 'dashboard-grid',
        })

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <SortableContext
                items={sortedWidgets.map((w) => w.id)}
                strategy={rectSortingStrategy}
                disabled={isLocked}
            >
                <div
                    ref={setDroppableRef}
                    className={`relative grid grid-cols-12 gap-2 min-h-[200px] transition-colors ${
                        isOverDashboard ? 'bg-primary/5 rounded-lg' : ''
                    }`}
                >
                    {sortedWidgets?.map((widget) => (
                        <SortableCard
                            key={widget.id}
                            widget={widget}
                            isLocked={isLocked}
                            onResize={handleResize}
                        >
                            {widget.component}
                        </SortableCard>
                    ))}
                </div>
            </SortableContext>

            <DragOverlay>
                {activeWidget ? (
                    <div
                        className={`${getGridSizeClass(activeWidget.colSpan || 6)} opacity-80 shadow-2xl rotate-2 scale-105`}
                    >
                        {activeWidget.component}
                    </div>
                ) : null}
            </DragOverlay>

            {/* Trash zone - only show when dragging and not locked */}
            {!isLocked && <TrashZone isActive={!!activeId} />}

            {/* Widget Store Drawer */}
            {department && (
                <WidgetStoreDrawer
                    activeWidgetIds={activeWidgetIds}
                    initialWidgetIds={initialWidgetIds}
                    onAddWidget={handleAddWidget}
                />
            )}
        </DndContext>
    )
}

export default DraggableLayout
